import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { isNative } from './platform';

type QueueItem = {
  id: string;
  kind: 'upload' | 'request';
  createdAt: string;
  payload: unknown;
};

const queueKey = 'mindcouncil:offline-queue';
const processors = new Map<QueueItem['kind'], (item: QueueItem) => Promise<boolean>>();

async function getStorageValue(key: string) {
  if (!isNative()) return window.localStorage.getItem(key);
  return (await Preferences.get({ key })).value;
}

async function setStorageValue(key: string, value: string) {
  if (!isNative()) {
    window.localStorage.setItem(key, value);
    return;
  }
  await Preferences.set({ key, value });
}

export async function cacheValue<T>(key: string, value: T) {
  await setStorageValue(`mindcouncil:cache:${key}`, JSON.stringify({ value, cachedAt: new Date().toISOString() }));
}

export async function readCachedValue<T>(key: string): Promise<T | null> {
  const raw = await getStorageValue(`mindcouncil:cache:${key}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw).value as T;
  } catch {
    return null;
  }
}

export async function enqueueOfflineItem(item: Omit<QueueItem, 'id' | 'createdAt'>) {
  const current = await readQueue();
  current.push({ ...item, id: crypto.randomUUID(), createdAt: new Date().toISOString() });
  await setStorageValue(queueKey, JSON.stringify(current));
}

export function registerOfflineQueueProcessor(kind: QueueItem['kind'], processor: (item: QueueItem) => Promise<boolean>) {
  processors.set(kind, processor);
  return () => processors.delete(kind);
}

export async function readQueue(): Promise<QueueItem[]> {
  const raw = await getStorageValue(queueKey);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as QueueItem[];
  } catch {
    return [];
  }
}

export async function syncOfflineQueues() {
  const status = isNative() ? await Network.getStatus() : { connected: navigator.onLine };
  if (!status.connected) return { synced: false, reason: 'offline' };
  const queue = await readQueue();
  if (queue.length === 0) return { synced: true, processed: 0 };
  const remaining: QueueItem[] = [];
  let processed = 0;

  for (const item of queue) {
    const processor = processors.get(item.kind);
    if (!processor) {
      remaining.push(item);
      continue;
    }

    try {
      const done = await processor(item);
      if (done) {
        processed += 1;
      } else {
        remaining.push(item);
      }
    } catch (error) {
      console.warn('[Offline] Queue processor failed', { kind: item.kind, id: item.id, error });
      remaining.push(item);
    }
  }

  await setStorageValue(queueKey, JSON.stringify(remaining));
  return { synced: true, processed, pending: remaining.length };
}
